import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Clock, Mail, Home, RefreshCw, AlertCircle } from 'lucide-react';
import { getPaymentInfo } from '../services/mercadopago';
import copy from '../copy';

interface PaymentInfo {
  id: string;
  status: string;
  status_detail: string;
  external_reference: string;
  amount: number;
  currency: string;
  payment_method: string;
  created_at: string;
}

const PaymentPending: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const paymentId = searchParams.get('payment_id');

  const formatPrice = (price: number) => `${copy.comun_simbolo}${price} ${copy.comun_moneda}`;

  useEffect(() => {
    const fetchPaymentInfo = async () => {
      if (!paymentId) {
        setError('ID de pago no encontrado');
        setLoading(false);
        return;
      }

      try {
        const payment = await getPaymentInfo(paymentId);
        setPaymentInfo({
          id: payment.id,
          status: payment.status,
          status_detail: payment.status_detail,
          external_reference: payment.external_reference,
          amount: payment.transaction_amount,
          currency: payment.currency_id,
          payment_method: payment.payment_method?.type || 'Desconocido',
          created_at: payment.date_created,
        });
      } catch (err) {
        console.error('Error fetching payment info:', err);
        setError('Error al cargar la información del pago');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentInfo();
  }, [paymentId]);

  const getPendingMessage = (statusDetail: string) => {
    const messages: { [key: string]: string } = {
      'pending_contingency': 'Estamos procesando tu pago. Te notificaremos cuando esté listo.',
      'pending_review_manual': 'Tu pago está en revisión. Te contactaremos pronto.',
      'pending_waiting_payment': 'Esperando confirmación del pago.',
      'pending_waiting_transfer': 'Esperando transferencia bancaria.',
      'pending_capture': 'Procesando autorización del pago.',
    };

    return messages[statusDetail] || 'Tu pago está siendo procesado. Te notificaremos cuando esté completo.';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">Verificando Pago...</h1>
          <p className="text-primary-600">Por favor espera mientras verificamos el estado del pago</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">Error</h1>
          <p className="text-primary-600 mb-6">{error}</p>
          <Link to="/carrito" className="btn-primary">
            Volver al Carrito
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 section-padding">
      <div className="container-max">
        <div className="max-w-4xl mx-auto">
          {/* Pending Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-yellow-600 animate-pulse" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary-900 mb-4">
              {copy.payment_pending_titulo}
            </h1>
            <p className="text-xl text-primary-600 max-w-2xl mx-auto">
              {copy.payment_pending_desc}. Te notificaremos cuando esté completo.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Payment Details */}
            {paymentInfo && (
              <div className="card-minimal">
                <h2 className="text-xl font-semibold text-primary-900 mb-6">
                  Detalles del Pago
                </h2>
                
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-primary-600">ID de Pago:</span>
                    <span className="font-medium text-primary-900">{paymentInfo.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Monto:</span>
                    <span className="font-semibold text-primary-900">
                      {formatPrice(paymentInfo.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Método de Pago:</span>
                    <span className="font-medium text-primary-900 capitalize">
                      {paymentInfo.payment_method}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Estado:</span>
                    <span className="font-medium text-yellow-600 capitalize">
                      Pendiente
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">Fecha:</span>
                    <span className="font-medium text-primary-900">
                      {new Date(paymentInfo.created_at).toLocaleDateString('es-MX')}
                    </span>
                  </div>
                </div>

                {/* Pending Status */}
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-yellow-800">Estado del Pago</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        {getPendingMessage(paymentInfo.status_detail)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* What happens next */}
            <div className="card-minimal">
              <h2 className="text-xl font-semibold text-primary-900 mb-6">
                ¿Qué sigue?
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary-900">Procesamiento</h3>
                    <p className="text-sm text-primary-600">
                      Tu pago está siendo procesado por nuestro sistema de pagos
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Mail className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary-900">Notificación</h3>
                    <p className="text-sm text-primary-600">
                      Te enviaremos un email cuando el pago sea confirmado o si necesitamos más información
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <RefreshCw className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-primary-900">Acceso a Cursos</h3>
                    <p className="text-sm text-primary-600">
                      Una vez confirmado el pago, tendrás acceso inmediato a tus cursos
                    </p>
                  </div>
                </div>
              </div>

              {/* Timeframe */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2">Tiempo Estimado</h3>
                <p className="text-sm text-blue-700">
                  Los pagos suelen procesarse en 24-48 horas. Si tienes alguna duda, no dudes en contactarnos.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="text-center mt-12 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => window.location.reload()} 
                className="btn-primary inline-flex items-center"
              >
                <RefreshCw className="mr-2 w-5 h-5" />
                Actualizar Estado
              </button>
              <Link to="/" className="btn-secondary inline-flex items-center">
                <Home className="mr-2 w-5 h-5" />
                Volver al Inicio
              </Link>
            </div>
            
            <p className="text-sm text-primary-500">
              ¿Necesitas ayuda? Contacta a nuestro equipo de soporte en{' '}
              <a href="mailto:support@iberotech.org" className="text-accent-600 hover:underline">
                support@iberotech.org
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPending; 